/**
 * dbtfy-send-for-visualization.js
 *
 * Powers the "Изпрати за визуализация" button.
 *
 * What it does, step by step:
 *  1. On click, finds the nearest Shopify product form on the page
 *     (the same form Aris Product Options writes its fields into).
 *  2. Pulls out the fields it cares about: an uploaded photo URL,
 *     a description/text field, email, and phone — matched loosely by
 *     common name patterns so this keeps working if Aris's exact field
 *     names change slightly. Adjust FIELD_MATCHERS below if needed.
 *  3. Validates that the required fields are present.
 *  4. POSTs everything (+ product id, variant id, shop domain) as JSON
 *     to the backend URL from Theme Settings.
 *  5. On success, adds the item to cart directly via Shopify's
 *     /cart/add.js, serializing the same form Aris wrote its line item
 *     properties into — then waits for that to finish before moving on.
 *  6. Redirects to /cart, where a banner (see
 *     sections/dbtfy-visualization-status.liquid) tells the customer
 *     their visualization is being worked on. The customer is never
 *     blocked from checking out immediately — approval happens entirely
 *     on the backend/fulfillment side, after payment if they choose to
 *     pay right away.
 *
 * This script does NOT create Shopify Draft Orders itself — that must
 * happen on the backend, which receives this POST request. See the
 * accompanying architecture notes for why that split exists.
 */
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  // Loose matchers for Aris-style field names/labels. Update these if your
  // Aris field setup uses different names — easiest fix is to add the exact
  // The real Aris (Avis Product Options) field names on this store, as
  // they appear in the cart form. Aris prefixes line-item properties with
  // "properties[...]". Photos come as multiple numbered fields
  // (Снимки-1, Снимки-2, ...), each holding a hosted image URL.
  var FIELD_MATCHERS = {
    // Description: "Име, цвят на очи и коса на всеки герой." (note the
    // trailing space Aris includes). Matched loosely to survive small
    // label edits.
    description: [/цвят на очи/i, /описание/i, /име.*коса/i],
    email: [/^properties\[Имейл\]$/i, /имейл/i, /email/i, /e-mail/i],
    phone: [/^properties\[Телефон\]$/i, /телефон/i, /phone/i, /tel/i]
  };

  // Collects ALL photo URLs from Aris's numbered "Снимки-N" fields.
  function collectPhotoUrls(form) {
    var urls = [];
    var inputs = form.querySelectorAll("input[name], textarea[name]");
    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].name || "";
      // Matches properties[Снимки-1], properties[Снимки-2], etc.
      if (/\[\s*Снимки\s*-\s*\d+\s*\]/i.test(name) || /(^|\[)\s*Снимки\s*-\s*\d+/i.test(name)) {
        var val = (inputs[i].value || "").trim();
        if (val) urls.push(val);
      }
    }
    return urls;
  }

  function matchField(form, matchers) {
    var candidates = form.querySelectorAll(
      "input[name], textarea[name], select[name]"
    );

    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var haystack = (el.name || "") + " " + (el.id || "") + " " + (el.getAttribute("data-label") || "") + " " + (el.placeholder || "");
      for (var m = 0; m < matchers.length; m++) {
        if (matchers[m].test(haystack)) {
          return el;
        }
      }
    }
    return null;
  }

  function getFieldValue(el) {
    if (!el) return "";
    if (el.type === "checkbox" || el.type === "radio") {
      return el.checked ? el.value : "";
    }
    return (el.value || "").trim();
  }

  function findProductForm(root) {
    // Aris and the theme's own add-to-cart both live inside
    // form[action*="/cart/add"] (see theme.ajaxCart settings.formSelector).
    return (
      root.closest("[data-product-form]") ||
      document.querySelector("form[action*='/cart/add']")
    );
  }

  function addToCartDirectly(form) {
    // Serialize the actual product form (id, quantity, and crucially all
    // of Aris's properties[...] fields) so the line item properties the
    // customer just filled in end up on the cart line, same as if they'd
    // clicked the native button themselves.
    var formData = new FormData(form);

    return fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (body) {
          throw new Error((body && body.description) || "cart_add_failed");
        });
      }
      return response.json();
    });
  }

  function setLoading(container, isLoading) {
    var button = container.querySelector("[data-dbtfy-send-for-viz-button]");
    var spinner = container.querySelector(".dbtfy-send-for-viz__spinner");
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle("is-loading", isLoading);
    if (spinner) spinner.hidden = !isLoading;
  }

  function showInlineError(container, message) {
    var el = container.querySelector("[data-dbtfy-send-for-viz-inline-error]");
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = "";
      return;
    }
    el.hidden = false;
    el.textContent = message;
  }

  function collectPayload(form, productId) {
    var photoUrls = collectPhotoUrls(form);
    var descriptionField = matchField(form, FIELD_MATCHERS.description);
    var emailField = matchField(form, FIELD_MATCHERS.email);
    var phoneField = matchField(form, FIELD_MATCHERS.phone);

    var variantInput = form.querySelector("[name='id']");

    return {
      shop: window.Shopify && window.Shopify.shop ? window.Shopify.shop : window.location.hostname,
      product_id: productId,
      variant_id: variantInput ? variantInput.value : null,
      // First photo URL goes in photo_url (backend's main field); all of
      // them go in photo_urls so the backend can attach every photo.
      photo_url: photoUrls.length ? photoUrls[0] : "",
      photo_urls: photoUrls,
      description: getFieldValue(descriptionField),
      email: getFieldValue(emailField),
      phone: getFieldValue(phoneField),
      page_url: window.location.href
    };
  }

  // Basic but real format checks — catches "Тест" in the email field and
  // "123456" in the phone field, which the old check let straight through
  // (it only verified the fields were non-empty, not that they looked like
  // a real email/phone). Bulgarian mobile numbers are matched loosely
  // enough to allow +359/00359/0 prefixes and spaces/dashes, while still
  // rejecting obvious junk like "123456".
  // The phone input sits next to a separate +359 country-code dropdown
  // (rendered by the Aris app), so el.value here is normally just the
  // local number the customer typed — e.g. "888888888". But older or
  // less tech-savvy customers sometimes type the leading 0 anyway, or
  // even the full +359/00359 prefix despite the dropdown already showing
  // it, or just leave off a digit. Rather than reject those, we accept
  // a generous range (8 to 10 digits after stripping any prefix) so the
  // check catches real junk like "123456" without tripping up someone
  // who reasonably guessed at the format.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isValidEmail(value) {
    return EMAIL_RE.test(value.trim());
  }

  function isValidPhone(value) {
    var digitsOnly = value.replace(/[\s()-]/g, "");
    // Strip an optional country-code prefix the customer may have typed
    // anyway (+359, 00359, or a leading 0), then check what's left is
    // 8-10 digits — generous enough for typos/extra-leading-zero cases
    // while still catching obvious junk like "123456".
    digitsOnly = digitsOnly.replace(/^(\+359|00359|359|0)/, "");
    return /^[0-9]{8,10}$/.test(digitsOnly);
  }

  function validatePayload(payload) {
    if (!payload.photo_url || !payload.description || !payload.email || !payload.phone) {
      return { ok: false, reason: "missing_fields" };
    }
    if (!isValidEmail(payload.email)) {
      return { ok: false, reason: "invalid_email" };
    }
    if (!isValidPhone(payload.phone)) {
      return { ok: false, reason: "invalid_phone" };
    }
    return { ok: true };
  }

  var SESSION_STORAGE_KEY = "dbtfy_last_visualization_submission";
  var STATUS_FLAG_KEY = "dbtfy_visualization_in_progress";

  function rememberSubmission(payload) {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          email: payload.email,
          phone: payload.phone,
          description: payload.description,
          photo_url: payload.photo_url
        })
      );
      // Tells the cart page to show the "working on your visualization"
      // banner. Cleared automatically the next time someone lands on the
      // cart with nothing pending (see dbtfy-visualization-status.js).
      sessionStorage.setItem(STATUS_FLAG_KEY, "1");
    } catch (e) {
      // sessionStorage can throw in some privacy modes — the redirect to
      // /cart still happens, the customer just won't see the banner.
    }
  }

  function sendForVisualization(container) {
    var apiUrl = container.getAttribute("data-api-url");
    var apiSecret = container.getAttribute("data-api-secret");
    var msgMissingApi = container.getAttribute("data-msg-missing-api");
    var msgGenericError = container.getAttribute("data-msg-generic-error");
    var msgMissingFields = container.getAttribute("data-msg-missing-fields");

    showInlineError(container, "");

    if (!apiUrl) {
      showInlineError(container, msgMissingApi);
      return;
    }

    var form = findProductForm(container);
    if (!form) {
      showInlineError(container, msgGenericError);
      return;
    }

    var productId = container.getAttribute("data-product-id");
    var payload = collectPayload(form, productId);

    var validation = validatePayload(payload);
    if (!validation.ok) {
      var msgInvalidEmail = container.getAttribute("data-msg-invalid-email");
      var msgInvalidPhone = container.getAttribute("data-msg-invalid-phone");
      if (validation.reason === "invalid_email") {
        showInlineError(container, msgInvalidEmail || msgMissingFields);
      } else if (validation.reason === "invalid_phone") {
        showInlineError(container, msgInvalidPhone || msgMissingFields);
      } else {
        showInlineError(container, msgMissingFields);
      }
      return;
    }

    setLoading(container, true);

    fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Visualization-Secret": apiSecret || ""
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Request failed with status " + response.status);
        return response.json().catch(function () { return {}; });
      })
      .then(function () {
        rememberSubmission(payload);

        // Add to cart directly and wait for it to actually finish before
        // redirecting — clicking the theme's native button and racing a
        // separate fetch alongside it isn't reliable, since there's no
        // guarantee which finishes first.
        return addToCartDirectly(form);
      })
      .then(function () {
        window.location.href = "/cart";
      })
      .catch(function () {
        showInlineError(container, msgGenericError);
      })
      .finally(function () {
        setLoading(container, false);
      });
  }

  ready(function () {
    // Mark the page as having the visualization button active, so the CSS
    // can hide the theme's native add-to-cart button (which still works
    // under the hood — we only hide it visually). We do this from JS so the
    // native button is never hidden on pages where, for any reason, our
    // button didn't initialize — those pages keep their normal button.
    var vizContainer = document.querySelector("[data-dbtfy-send-for-viz]");
    if (vizContainer) {
      document.body.classList.add("dbtfy-viz-active");

      // WARM-UP PING: Render's free tier spins the backend down after 15
      // minutes of inactivity, and the first request then takes 30-60s
      // (cold start) — long enough for a customer to give up. So the
      // moment someone opens a product page, we quietly ping the backend's
      // /health endpoint in the background. By the time they've picked a
      // photo and typed their details (usually 1-3 minutes), the server is
      // awake and the button responds in ~1-2 seconds. Fire-and-forget:
      // errors are ignored, this must never affect the page.
      var apiUrl = vizContainer.getAttribute("data-api-url") || "";
      if (apiUrl) {
        try {
          var origin = new URL(apiUrl).origin;
          fetch(origin + "/health", { method: "GET", mode: "cors" }).catch(function () {});
        } catch (e) {
          /* malformed URL — ignore */
        }
      }
    }

    document.addEventListener("click", function (event) {
      var trigger = event.target.closest("[data-dbtfy-send-for-viz-button]");
      if (trigger) {
        var container = trigger.closest("[data-dbtfy-send-for-viz]");
        if (container) sendForVisualization(container);
      }
    });
  });
})();
